<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import UploadPicker, { PickedUpload } from '@/components/upload/UploadPicker.vue'
import CsvEbomLoader from '@/components/ebom/CsvEbomLoader.vue'
import StepEbomLoader from '@/components/ebom/StepEbomLoader.vue'
import PromoteEbomParts from '@/components/ebom/PromoteEbomParts.vue'
import EbomPartAllocationSuggestionsLoader from '@/components/ebom/EbomPartAllocationSuggestionsLoader.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import useContentTypes from '@/composables/useContentTypes'

const props = defineProps<{
  bopId: string
}>()
const emit = defineEmits<{
  done: []
}>()

const { t } = useI18n()
const { contentTypesCsv, contentTypesStep } = useContentTypes()
const isOpen = ref(false)

enum STEP {
  PICK = 1,
  LOAD = 2,
  PARTS = 3,
  SUGGESTIONS = 4,
  DONE = 5,
}
const stepperModel = ref<STEP>(STEP.PICK)
watch(isOpen, (v) => {
  if (v) {
    stepperModel.value = STEP.PICK
  }
})
function isComplete(currentStep: STEP) {
  return stepperModel.value > currentStep
}

const uploadPickerOpen = ref(false)
const uploadPickerMimeTypesFilter = ref<string[]>([])
const uploadPicked = ref<PickedUpload>()
function onUpload(upload: PickedUpload) {
  uploadPicked.value = upload
  stepperModel.value = STEP.LOAD
}

enum TYPE {
  CSV,
  STEP,
}
const pickedType = ref<TYPE>()
function pickType(type: TYPE) {
  switch (type) {
    case TYPE.CSV:
      uploadPickerMimeTypesFilter.value = contentTypesCsv
      break
    case TYPE.STEP:
      uploadPickerMimeTypesFilter.value = contentTypesStep
      break
  }

  pickedType.value = type
  uploadPickerOpen.value = true
}

const ebomId = ref<string>()
function onLoaded(newEbomId: string) {
  ebomId.value = newEbomId
  stepperModel.value = STEP.PARTS
}

function onEbomPartsPromoted() {
  stepperModel.value = STEP.SUGGESTIONS
}

function onEbomPartAllocationSuggestionsDone() {
  stepperModel.value = STEP.DONE
}

function onDone() {
  emit('done')
  isOpen.value = false
}
</script>

<template>
  <data-table-create-button @click="isOpen = true" />

  <v-stepper v-model="stepperModel">
    <v-dialog :model-value="isOpen" persistent scrollable>
      <v-card>
        <v-card-title class="stepper-title">
          <v-stepper-header>
            <v-stepper-item
              :value="STEP.PICK"
              :complete="isComplete(STEP.PICK)"
              :title="t('component.ebomLoader.titlePickEbom')"
            />
            <v-divider />
            <v-stepper-item
              :value="STEP.LOAD"
              :complete="isComplete(STEP.LOAD)"
              :title="t('component.ebomLoader.titleLoadEbom')"
            />
            <v-divider />
            <v-stepper-item
              :value="STEP.PARTS"
              :complete="isComplete(STEP.PARTS)"
              :title="t('component.ebomLoader.titlePromoteEbomParts')"
            />
            <v-divider />
            <v-stepper-item
              :value="STEP.SUGGESTIONS"
              :complete="isComplete(STEP.SUGGESTIONS)"
              :title="t('component.ebomLoader.titleEbomPartAllocationSuggestions')"
            />
            <v-divider />
            <v-stepper-item
              :value="STEP.DONE"
              :complete="isComplete(STEP.DONE)"
              :title="t('component.ebomLoader.titleDone')"
            />
          </v-stepper-header>
        </v-card-title>
        <v-card-text class="stepper-text">
          <v-stepper-window>
            <v-stepper-window-item :value="STEP.PICK">
              <v-row justify="center" class="pa-4">
                <v-col cols="5">
                  <v-card
                    variant="outlined"
                    hover
                    class="pick-card text-center pa-6"
                    @click="pickType(TYPE.CSV)"
                  >
                    <v-icon icon="table_chart" size="48" color="primary" class="mb-3" />
                    <div class="text-h6">{{ t('component.ebomLoader.loadEbomFromCsv') }}</div>
                    <div class="text-body-2 text-medium-emphasis mt-1">.csv</div>
                  </v-card>
                </v-col>
                <v-col cols="5">
                  <v-card
                    variant="outlined"
                    hover
                    class="pick-card text-center pa-6"
                    @click="pickType(TYPE.STEP)"
                  >
                    <v-icon icon="view_in_ar" size="48" color="primary" class="mb-3" />
                    <div class="text-h6">{{ t('component.ebomLoader.loadEbomFromStepFile') }}</div>
                    <div class="text-body-2 text-medium-emphasis mt-1">.step / .stp</div>
                  </v-card>
                </v-col>
              </v-row>
            </v-stepper-window-item>
            <v-stepper-window-item :value="STEP.LOAD">
              <csv-ebom-loader
                v-if="pickedType == TYPE.CSV && !!uploadPicked"
                :bop-id="props.bopId"
                :upload-id="uploadPicked.id"
                @done="onLoaded"
              />
              <step-ebom-loader
                v-if="pickedType == TYPE.STEP && !!uploadPicked"
                :bop-id="props.bopId"
                :upload-id="uploadPicked.id"
                @done="onLoaded"
              />
            </v-stepper-window-item>
            <v-stepper-window-item :value="STEP.PARTS">
              <promote-ebom-parts v-if="ebomId" :ebom-id="ebomId" @done="onEbomPartsPromoted" />
            </v-stepper-window-item>
            <v-stepper-window-item :value="STEP.SUGGESTIONS">
              <ebom-part-allocation-suggestions-loader
                v-if="ebomId"
                :ebom-id="ebomId"
                @done="onEbomPartAllocationSuggestionsDone()"
              />
            </v-stepper-window-item>
            <v-stepper-window-item :value="STEP.DONE">
              <v-row>
                <v-col offset="2" cols="8" class="text-center">
                  <v-icon icon="verified" color="green" size="60" class="mt-3 mb-3" />
                  <h2>{{ t('component.ebomLoader.doneHeader') }}</h2>
                  <p>{{ t('component.ebomLoader.doneMessage') }}</p>

                  <v-btn class="mt-5" color="primary" variant="elevated" @click="onDone()">
                    {{ t('component.ebomLoader.doneButton') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="stepperModel != STEP.DONE" variant="flat" :disabled="false" @click="onDone">
            {{ t('button.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-stepper>

  <upload-picker
    v-if="uploadPickerOpen"
    v-model="uploadPickerOpen"
    :mime-types="uploadPickerMimeTypesFilter"
    pick-one
    @pick-one="onUpload"
  />
</template>

<style scoped lang="scss">
.stepper-title {
  padding: 0;
}
.stepper-text {
  padding: 0 !important;
}
.pick-card {
  cursor: pointer;
  transition:
    border-color 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
  }
}
</style>
